<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useUserStore } from '@/stores/user'
import HeaderPanel from '@/components/HeaderPanel.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import ToggleSwitch from 'primevue/toggleswitch'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import Select from 'primevue/select'
import { cloneDefaultTemplate, sheetExportsApi } from '@/services/sheetExportsApi'

const FALLBACK_PARAMS = [
  { key: 'name', label: 'Имя' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Телефон' },
  { key: 'username', label: 'Telegram username' },
  { key: 'utm_source', label: 'utm_source' },
  { key: 'utm_medium', label: 'utm_medium' },
  { key: 'utm_campaign', label: 'utm_campaign' },
  { key: 'utm_content', label: 'utm_content' },
  { key: 'utm_term', label: 'utm_term' },
  { key: 'number', label: 'Номер заказа' },
  { key: 'date', label: 'Дата' },
  { key: 'cost_money', label: 'Стоимость' },
  { key: 'payed_money', label: 'Оплачено' },
  { key: 'sb_id', label: 'sb_id' },
  { key: 'tg_id', label: 'tg_id' },
]

const userStore = useUserStore()
const toast = useToast()
const confirm = useConfirm()

const loading = ref(false)
const saving = ref(false)
const checking = ref(false)
const rows = ref([])
const params = ref(FALLBACK_PARAMS)
const serviceAccountEmail = ref('')

const editorVisible = ref(false)
const editingId = ref(null)
const skipSheetFetch = ref(false)
const sheetOptions = ref([])
const headerOptions = ref([])
const form = ref(emptyForm())

const hookVisible = ref(false)
const hookUrl = ref('')
const hookExportId = ref(null)

const logVisible = ref(false)
const logLoading = ref(false)
const logRows = ref([])
const logTitle = ref('')

const editorTitle = computed(() => (editingId.value ? 'Изменить выгрузку' : 'Создать выгрузку'))

function emptyMapping() {
  const mapping = {}
  params.value.forEach((item) => {
    mapping[item.key] = ''
  })
  return mapping
}

function emptyForm() {
  return {
    name: '',
    process_url: '',
    spreadsheet: '',
    sheet_name: '',
    mapping: {},
  }
}

function apiMessage(error) {
  return error.response?.data?.message || error.response?.data?.error || error.message
}

function mappedCount(mapping) {
  return Object.values(mapping || {}).filter((value) => String(value || '').trim()).length
}

function sheetLink(id) {
  return `https://docs.google.com/spreadsheets/d/${id}/edit`
}

function columnOptions(current) {
  const options = [{ label: 'не выгружать', value: '' }]
  headerOptions.value.forEach((header) => {
    options.push({
      label: `${header.letter}: ${header.title}`,
      value: header.title,
    })
  })
  if (current && !options.some((option) => option.value === current)) {
    options.push({ label: current, value: current })
  }
  return options
}

async function loadMeta() {
  try {
    const { data } = await sheetExportsApi.meta()
    const meta = data.data || {}
    if (Array.isArray(meta.params) && meta.params.length) params.value = meta.params
    serviceAccountEmail.value = meta.serviceAccountEmail || ''
  } catch (error) {
    toast.add({ severity: 'warn', summary: 'Подсказка', detail: apiMessage(error), life: 4000 })
  }
}

async function loadList() {
  loading.value = true
  try {
    const { data } = await sheetExportsApi.list()
    rows.value = data.data || []
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Список', detail: apiMessage(error), life: 4000 })
  } finally {
    loading.value = false
  }
}

async function openCreate() {
  editingId.value = null
  sheetOptions.value = []
  headerOptions.value = []
  form.value = cloneDefaultTemplate()
  editorVisible.value = true
  await checkAccess()
}

function openEdit(row) {
  editingId.value = row.id
  sheetOptions.value = row.sheet_name ? [{ title: row.sheet_name }] : []
  headerOptions.value = []
  const mapping = emptyMapping()
  Object.entries(row.mapping || {}).forEach(([key, value]) => {
    if (Object.prototype.hasOwnProperty.call(mapping, key)) mapping[key] = value || ''
  })
  form.value = {
    name: row.name,
    process_url: row.process_url || '',
    spreadsheet: row.spreadsheet_id,
    sheet_name: row.sheet_name,
    mapping,
  }
  editorVisible.value = true
}

async function checkAccess() {
  if (!form.value.spreadsheet.trim()) {
    toast.add({ severity: 'warn', summary: 'Таблица', detail: 'Вставьте ссылку или id', life: 3000 })
    return
  }
  checking.value = true
  try {
    const { data } = await sheetExportsApi.checkSheet({
      spreadsheet: form.value.spreadsheet.trim(),
    })
    const payload = data.data || {}
    sheetOptions.value = payload.sheets || []
    const titles = sheetOptions.value.map((sheet) => sheet.title)
    const preferred = titles.includes(payload.defaultSheetName)
      ? payload.defaultSheetName
      : (titles[0] || '')
    skipSheetFetch.value = true
    form.value.sheet_name = preferred
    headerOptions.value = payload.headers || []
    if (payload.spreadsheetId) form.value.spreadsheet = payload.spreadsheetId
    toast.add({ severity: 'success', summary: 'Доступ есть', detail: `Листов: ${sheetOptions.value.length}`, life: 2500 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Таблица', detail: apiMessage(error), life: 5000 })
  } finally {
    checking.value = false
    await nextTick()
    await nextTick()
    skipSheetFetch.value = false
  }
}

async function onSheetChange() {
  if (skipSheetFetch.value || checking.value) return
  if (!form.value.spreadsheet || !form.value.sheet_name) return
  checking.value = true
  try {
    const { data } = await sheetExportsApi.checkSheet({
      spreadsheet: form.value.spreadsheet,
      sheetName: form.value.sheet_name,
    })
    headerOptions.value = data.data?.headers || []
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Лист', detail: apiMessage(error), life: 4000 })
  } finally {
    checking.value = false
  }
}

async function saveExport() {
  if (!form.value.name.trim() || !form.value.spreadsheet.trim() || !form.value.sheet_name || !mappedCount(form.value.mapping)) {
    toast.add({
      severity: 'warn',
      summary: 'Проверьте форму',
      detail: 'Нужны название, таблица, лист и хотя бы один параметр',
      life: 3500,
    })
    return
  }
  const payload = {
    name: form.value.name.trim(),
    process_url: form.value.process_url.trim(),
    spreadsheet: form.value.spreadsheet.trim(),
    sheet_name: form.value.sheet_name,
    mapping: form.value.mapping,
  }
  saving.value = true
  try {
    if (editingId.value) {
      await sheetExportsApi.update(editingId.value, payload)
    } else {
      await sheetExportsApi.create(payload)
    }
    editorVisible.value = false
    await loadList()
    toast.add({ severity: 'success', summary: 'Выгрузка', detail: 'Сохранено', life: 2500 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Выгрузка', detail: apiMessage(error), life: 4000 })
  } finally {
    saving.value = false
  }
}

async function toggleEnabled(row, enabled) {
  const previous = row.enabled
  row.enabled = enabled
  try {
    await sheetExportsApi.update(row.id, { enabled })
  } catch (error) {
    row.enabled = previous
    toast.add({ severity: 'error', summary: 'Выгрузка', detail: apiMessage(error), life: 4000 })
  }
}

function openHook(row) {
  hookExportId.value = row.id
  hookUrl.value = row.hook_template
  hookVisible.value = true
}

async function copyHook() {
  try {
    await navigator.clipboard.writeText(hookUrl.value)
    toast.add({ severity: 'success', summary: 'Вебхук', detail: 'Скопировано', life: 2000 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Вебхук', detail: 'Не удалось скопировать', life: 3000 })
  }
}

async function rotateHook() {
  if (!hookExportId.value) return
  try {
    const { data } = await sheetExportsApi.rotateToken(hookExportId.value)
    hookUrl.value = data.data?.hook_template || hookUrl.value
    await loadList()
    toast.add({ severity: 'success', summary: 'Вебхук', detail: 'Ссылка перевыпущена', life: 2500 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Вебхук', detail: apiMessage(error), life: 4000 })
  }
}

async function openLog(row) {
  logTitle.value = row.name
  logRows.value = []
  logVisible.value = true
  logLoading.value = true
  try {
    const { data } = await sheetExportsApi.log(row.id)
    logRows.value = data.data || []
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Журнал', detail: apiMessage(error), life: 4000 })
  } finally {
    logLoading.value = false
  }
}

async function sendTest(row) {
  try {
    await sheetExportsApi.test(row.id)
    toast.add({ severity: 'success', summary: 'Тест', detail: 'Строка поставлена в очередь', life: 2500 })
    await loadList()
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Тест', detail: apiMessage(error), life: 4000 })
  }
}

function confirmDelete(row) {
  confirm.require({
    message: `Удалить выгрузку «${row.name}» вместе с очередью?`,
    header: 'Удаление',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Удалить',
    rejectLabel: 'Отмена',
    accept: async () => {
      try {
        await sheetExportsApi.remove(row.id)
        await loadList()
        toast.add({ severity: 'success', summary: 'Выгрузка', detail: 'Удалена', life: 2500 })
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Выгрузка', detail: apiMessage(error), life: 4000 })
      }
    },
  })
}

function payloadText(payload) {
  try {
    return JSON.stringify(payload)
  } catch {
    return ''
  }
}

function statusSeverity(status) {
  if (status === 'sent') return 'success'
  if (status === 'failed') return 'danger'
  return 'warn'
}

let gidTimer = null
watch(() => form.value.spreadsheet, (value) => {
  if (!editorVisible.value || checking.value) return
  if (!/[#?&]gid=\d+/.test(String(value || ''))) return
  clearTimeout(gidTimer)
  gidTimer = setTimeout(() => {
    checkAccess()
  }, 400)
})

onMounted(async () => {
  await userStore.checkAuth()
  await Promise.all([loadMeta(), loadList()])
})
</script>

<template>
  <ToastVue />
  <ConfirmDialog />
  <HeaderPanel />
  <div class="sheet-exports-page">
    <header class="page-header">
      <div>
        <h1>Выгрузки в Google Таблицы</h1>
        <p class="lead">Вебхук ГетКурса складывает строки в очередь, воркер дописывает их в таблицу батчами.</p>
      </div>
      <Button label="Создать выгрузку" icon="pi pi-plus" @click="openCreate" />
    </header>

    <Message severity="info" :closable="false">
      Расшарьте таблицу на сервисный аккаунт с правом редактора:
      <strong>{{ serviceAccountEmail || 'email не прочитан, проверьте SERVICE_ACCOUNT_FILE' }}</strong>
    </Message>

    <div class="panel-toolbar">
      <Button label="Обновить" icon="pi pi-refresh" severity="secondary" text :loading="loading" @click="loadList" />
    </div>

    <DataTable :value="rows" :loading="loading" size="small" scrollable>
      <Column field="name" header="Название" style="min-width: 10rem" />
      <Column header="Процесс" style="min-width: 8rem">
        <template #body="{ data }">
          <a v-if="data.process_url" :href="data.process_url" target="_blank" rel="noopener">процесс</a>
          <span v-else>—</span>
        </template>
      </Column>
      <Column header="Таблица" style="min-width: 8rem">
        <template #body="{ data }">
          <a :href="sheetLink(data.spreadsheet_id)" target="_blank" rel="noopener">таблица</a>
        </template>
      </Column>
      <Column field="sheet_name" header="Лист" />
      <Column header="Колонок">
        <template #body="{ data }">{{ mappedCount(data.mapping) }}</template>
      </Column>
      <Column header="Очередь" style="min-width: 14rem">
        <template #body="{ data }">
          <div class="status-tags">
            <Tag :value="`pending ${data.stats?.pending || 0}`" severity="warn" />
            <Tag :value="`sent ${data.stats?.sent || 0}`" severity="success" />
            <Tag :value="`failed ${data.stats?.failed || 0}`" severity="danger" />
          </div>
        </template>
      </Column>
      <Column header="Включено">
        <template #body="{ data }">
          <ToggleSwitch :modelValue="data.enabled" @update:modelValue="toggleEnabled(data, $event)" />
        </template>
      </Column>
      <Column header="Действия" style="min-width: 22rem">
        <template #body="{ data }">
          <div class="row-actions">
            <Button label="Вебхук" icon="pi pi-link" size="small" text @click="openHook(data)" />
            <Button label="Журнал" icon="pi pi-list" size="small" text @click="openLog(data)" />
            <Button label="Тест" icon="pi pi-play" size="small" text @click="sendTest(data)" />
            <Button label="Изменить" icon="pi pi-pencil" size="small" text @click="openEdit(data)" />
            <Button label="Удалить" icon="pi pi-trash" size="small" text severity="danger" @click="confirmDelete(data)" />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog v-model:visible="editorVisible" :header="editorTitle" modal :style="{ width: 'min(760px, 96vw)' }">
      <div class="form-grid">
        <label>Название</label>
        <InputText v-model="form.name" class="w-full" />

        <label>Ссылка на процесс в ГетКурсе</label>
        <InputText v-model="form.process_url" class="w-full" placeholder="https://" />

        <label>Ссылка или id Google-таблицы</label>
        <div class="inline-field">
          <InputText v-model="form.spreadsheet" class="w-full" />
          <Button label="Проверить доступ" icon="pi pi-check" severity="secondary" :loading="checking" @click="checkAccess" />
        </div>

        <label>Лист</label>
        <Select
          v-if="sheetOptions.length"
          v-model="form.sheet_name"
          :options="sheetOptions"
          optionLabel="title"
          optionValue="title"
          placeholder="Выберите лист"
          class="w-full"
          @update:modelValue="onSheetChange"
        />
        <InputText v-else v-model="form.sheet_name" class="w-full" placeholder="Сначала проверьте доступ" />

        <h3>Параметры → колонки</h3>
        <p class="hint">Заголовки берутся из первой строки листа. «Не выгружать» — параметр в таблицу не попадёт.</p>
        <DataTable :value="params" size="small">
          <Column field="label" header="Параметр" />
          <Column field="key" header="Ключ" />
          <Column header="Колонка">
            <template #body="{ data }">
              <Select
                v-model="form.mapping[data.key]"
                :options="columnOptions(form.mapping[data.key])"
                optionLabel="label"
                optionValue="value"
                placeholder="не выгружать"
                class="w-full"
              />
            </template>
          </Column>
        </DataTable>
      </div>
      <template #footer>
        <Button label="Отмена" severity="secondary" text @click="editorVisible = false" />
        <Button label="Сохранить" :loading="saving" @click="saveExport" />
      </template>
    </Dialog>

    <Dialog v-model:visible="hookVisible" header="Вебхук" modal :style="{ width: 'min(840px, 96vw)' }">
      <p class="hint">Вставьте ссылку в процесс ГетКурса как есть. Параметры и порядок не меняйте.</p>
      <Textarea :modelValue="hookUrl" readonly rows="6" class="w-full hook-box" />
      <template #footer>
        <Button label="Перевыпустить" severity="secondary" text @click="rotateHook" />
        <Button label="Копировать" icon="pi pi-copy" @click="copyHook" />
      </template>
    </Dialog>

    <Dialog v-model:visible="logVisible" :header="`Журнал: ${logTitle}`" modal :style="{ width: 'min(960px, 96vw)' }">
      <DataTable :value="logRows" :loading="logLoading" size="small" scrollable>
        <Column field="id" header="id" />
        <Column header="Статус">
          <template #body="{ data }">
            <Tag :value="data.status" :severity="statusSeverity(data.status)" />
          </template>
        </Column>
        <Column field="attempts" header="Попытки" />
        <Column header="Данные" style="min-width: 16rem">
          <template #body="{ data }">
            <span class="payload">{{ payloadText(data.payload) }}</span>
          </template>
        </Column>
        <Column field="last_error" header="Ошибка" />
        <Column field="created_at" header="Создано" />
      </DataTable>
    </Dialog>
  </div>
</template>

<style scoped>
.sheet-exports-page {
  padding: 1rem 1.25rem 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 1.5rem;
}

.lead {
  margin: 0;
  color: #64748b;
}

.panel-toolbar {
  display: flex;
  gap: 0.5rem;
  margin: 0.75rem 0;
}

.status-tags,
.row-actions,
.inline-field {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.inline-field {
  width: 100%;
}

.form-grid {
  display: grid;
  gap: 0.55rem;
}

.form-grid h3 {
  margin: 0.75rem 0 0;
}

.hint {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.w-full {
  width: 100%;
}

.hook-box {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  word-break: break-all;
}

.payload {
  display: inline-block;
  max-width: 28rem;
  white-space: normal;
  word-break: break-word;
}
</style>
